// React
import React, { useState } from "react";

// External Packages
import { Draggable } from "react-beautiful-dnd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faHand,
  faExpandArrowsAlt, // Import the move icon
} from "@fortawesome/free-solid-svg-icons";

// Internal Components
import {
  OneIcon,
  TwoIcon,
  ThreeIcon,
  FourIcon,
  FiveIcon,
  SixIcon,
  SevenIcon,
  EightIcon,
  NineIcon,
  TenIcon,
} from "../../../../components/ui/Icons/Icon";

import QuoteList from "./List";
import Sort from "../Prioritisation/Sort";
import "../../objective.css";
import Completed from "../Completed/Completed";

const Column = ({
  index,
  title,
  order,
  objective,
  overlay,
  isScrollable,
  isCombineEnabled,
  useClone,
  updateTitle,
  handleUrgencyChange,
  handleEstimateChange,
  handleSort,
  display,
  addSubTask,
  addStep,
  aiHelp,
  toggleSubTask,
  toggleStep,
  toggleOverlay,
  deleteObjective,
  deleteStep,
  deleteSubtask,
  updateDescription,
  updateStep,
  updateNotes,
  onDragEndSteps,
  viewType,
  day, // Provided by Board.js, true if on Today page
  columnType,
  cyclePosition, // Function to move the board (passed from Board.js)
  promoteStepToMoment,
  momentValues,
}) => {
  const [warning, setWarning] = useState(false);
  const [sort, setSort] = useState({
    urge: { active: false, type: "asc" },
    dead: { active: false, type: "asc" },
    cust: { active: false },
  });

  const sortHandler = (e) => {
    const targetData = e.currentTarget.dataset;
    let sortStyle = targetData.name;

    setSort((prevSort) => {
      let shallow = { ...prevSort };
      if (sort[sortStyle].active) {
        shallow[sortStyle].type =
          shallow[sortStyle].type === "asc" ? "desc" : "asc";
      } else {
        shallow[sortStyle].active = !shallow[sortStyle].active;
      }

      Object.keys(shallow).forEach((key) => {
        if (key !== sortStyle) {
          shallow[key].active = false;
        }
      });
      return shallow;
    });
  };

  let numIconObj = {
    "obj-one": <OneIcon />,
    "obj-two": <TwoIcon />,
    "obj-three": <ThreeIcon />,
    "obj-four": <FourIcon />,
    "obj-five": <FiveIcon />,
    "obj-six": <SixIcon />,
    "obj-seven": <SevenIcon />,
    "obj-eight": <EightIcon />,
    "obj-nine": <NineIcon />,
    "obj-ten": <TenIcon />,
  };

  // Determine if drag should be disabled based on 'day' prop
  const isDragDisabled = !!day;

  return (
    <>
      <Draggable
        draggableId={title && title}
        index={index}
        // 1. Disable dragging if we are on the 'Day' view
        isDragDisabled={isDragDisabled}
      >
        {(provided, snapshot) => (
          <div
            className={
              columnType === "week--mobile" || columnType === "day--mobile"
                ? `objective__col ${display?.[title] && "activeCol"} ${
                    objective?.title === "" && `hideColumn`
                  } ${columnType}`
                : columnType === "day--desktop"
                  ? `objective__col ${
                      display?.[title] ? "activeCol" : "inactiveCol"
                    } ${
                      objective?.title === "" && `hideColumn`
                    } ${columnType}`
                  : `objective__col ${columnType} ${
                      objective?.title === "" && `hideColumn`
                    }`
            }
            ref={provided.innerRef}
            {...provided.draggableProps}
          >
            <div
              className="objective__col--name"
              data-is-dragging={snapshot.isDragging}
            >
              <div className="col__name--icon">
                {title && numIconObj[title]}
              </div>
              <div className="col__name--trash">
                <FontAwesomeIcon
                  icon={faTrash}
                  onClick={() => setWarning(true)}
                />
              </div>
              <input
                aria-label={`${objective?.title} quote list`}
                type="text"
                className="col__name--input upper"
                value={objective?.title}
                placeholder="Objective Name"
                name={`${title}`}
                onChange={(event) => updateTitle(event, order)}
                autoComplete="off"
              ></input>

              {/* 2. LOGIC SWITCH: Hand (Week) vs Move Button (Today) */}
              {day ? (
                // TODAY VIEW: Button to cycle board position
                <div
                  className="col__name--hand"
                  onClick={cyclePosition}
                  style={{ cursor: "pointer" }}
                  title="Move Board"
                >
                  <FontAwesomeIcon icon={faExpandArrowsAlt} size="lg" />
                </div>
              ) : (
                // WEEK VIEW: Drag Handle for the column
                <div
                  className="col__name--hand"
                  {...provided.dragHandleProps}
                >
                  <FontAwesomeIcon icon={faHand} size="lg" />
                </div>
              )}
            </div>

            <QuoteList
              listId={title}
              listType="QUOTE"
              columnType={columnType}
              day={day}
              style={{
                backgroundColor: snapshot.isDragging
                  ? "rgba(0,74,34,0.1)"
                  : null,
              }}
              overlay={overlay}
              title={title}
              index={index}
              tasks={objective?.subTasks}
              internalScroll={isScrollable}
              isCombineEnabled={Boolean(isCombineEnabled)}
              handleUrgencyChange={handleUrgencyChange}
              handleEstimateChange={handleEstimateChange}
              useClone={Boolean(useClone)}
              onDragEndSteps={onDragEndSteps}
              addStep={addStep}
              aiHelp={aiHelp}
              toggleSubTask={toggleSubTask}
              toggleStep={toggleStep}
              toggleOverlay={toggleOverlay}
              updateDescription={updateDescription}
              updateStep={updateStep}
              updateNotes={updateNotes}
              deleteStep={deleteStep}
              deleteObjective={deleteObjective}
              deleteSubtask={deleteSubtask}
              promoteStepToMoment={promoteStepToMoment}
              momentValues={momentValues}
              viewType={viewType}
            />
            <Completed
              title={title}
              objective={objective}
              toggleSubTask={toggleSubTask}
              deleteSubtask={deleteSubtask}
            />
            <Sort
              sort={sort}
              sortHandler={sortHandler}
              handleSort={handleSort}
              title={title}
            />
            <div className="objective__col--name">
              <FontAwesomeIcon
                className="objective__col--icon"
                icon={faPlus}
              />
              <input
                type="text"
                className="col__name--input"
                placeholder="Add new action"
                name={`${title}`}
                autoComplete="off"
                onKeyDown={(event) => {
                  if (event.key === "Enter" && event.target.value !== "") {
                    event.preventDefault();
                    addSubTask(event);
                  }
                }}
              />
            </div>
          </div>
        )}
      </Draggable>
      <div
        className={warning ? "warning__callout--active" : "warning__callout"}
      >
        <strong>Are you sure you want to delete this objective?</strong>
        <div className="warning__button--row">
          <div
            className="callout__button"
            onClick={() => {
              deleteObjective(title);
              setWarning(false);
            }}
          >
            Yes
          </div>
          <div className="callout__button" onClick={() => setWarning(false)}>
            No
          </div>
        </div>
      </div>
    </>
  );
};

export default Column;
